/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useState } from "react";
import { StoryCard } from "../../Molecules/StoryCard";
import { CollectionName } from "../../Atoms/CollectionName";
import { getTextColor, getSlot } from "../../../utils/utils";
import { LoadmoreButton } from "../../Atoms/Loadmore";
import { collectionToStories } from "@quintype/components";
import { HeroImage } from "../../Atoms/HeroImage";
import { StorycardContent } from "../../Molecules/StorycardContent";
import { Headline } from "../../Atoms/Headline";
import { AuthorWithTime } from "../../Atoms/AuthorWithTimestamp";
import { SectionTag } from "../../Atoms/SectionTag";
import PropTypes from "prop-types";
import { StateProvider } from "../../SharedContext";
import "./one-col-story-list.m.css";
import { Subheadline } from "../../Atoms/Subheadline";
import get from "lodash.get";

const OneColStoryList = ({ collection, config = {} }) => {
  const {
    collectionNameBorderColor = "",
    borderColor = "",
    theme = "",
    border = "",
    slotConfig = [],
    collectionNameTemplate = "",
    footerSlotConfig = {},
    footerButton = "",
    initialLoadCount = 6
  } = config;
  const { footerSlot } = footerSlotConfig;
  const { type, component } = get(slotConfig, [0], {});
  const storyItems = collectionToStories(collection);
  const [sliceValue, setSliceValue] = useState(initialLoadCount);
  if (storyItems.length < 1) {
    return null;
  }
  const onClick = () => {
    setSliceValue(sliceValue + 8);
  };
  const footerSlotComp = footerSlot ? footerSlot() : null;
  const borderHandler = (story) => {
    switch (border) {
      case "bottom":
        return (
          <div styleName="one-col-border-bottom">
            <StoryCard
              story={story}
              theme={theme}
              headerLevel="4"
              isHorizontal
              border={border}
              aspectRatio={[[1, 1], [4, 3]]}
              borderColor={borderColor}
              prefix="By"
            />
          </div>
        );
      case "full":
        return (
          <div styleName="one-col-border-full">
            <StoryCard story={story} border={border} theme={theme} isHorizontal borderColor={borderColor}>
              <HeroImage story={story} isHorizontal aspectRatio={[[1, 1], [4, 3]]} />
              <div styleName="story-card-content-wrapper">
                <StorycardContent story={story} borderColor={borderColor}>
                  <SectionTag story={story} />
                  <Headline story={story} headerLevel="4" />
                  <Subheadline story={story} />
                  <AuthorWithTime story={story} prefix="By" />
                </StorycardContent>
              </div>
            </StoryCard>
          </div>
        );
      default:
        return (
          <div styleName="one-col-border-default">
            <StoryCard
              story={story}
              theme={theme}
              headerLevel="4"
              isHorizontal
              border={border}
              aspectRatio={[[1, 1], [4, 3]]}
              borderColor={borderColor}
              prefix="By"
            />
          </div>
        );
    }
  };

  const textColor = getTextColor(theme);
  return (
    <div className="full-width-with-padding arrow-component" style={{ backgroundColor: theme, color: textColor }}>
      <div styleName="one-col-story-list-wrapper ">
        <CollectionName
          collection={collection}
          collectionNameTemplate={collectionNameTemplate}
          collectionNameBorderColor={collectionNameBorderColor}
        />
        <div styleName="wrapper" style={{ backgroundColor: theme, color: textColor }}>
          <div styleName="one-col-first-story">
            {storyItems.slice(0, 1).map((story, index) => {
              return <div key={`default-${index}`}>{borderHandler(story)}</div>;
            })}
          </div>
          <div styleName="one-col-story-list">
            {storyItems.slice(1, sliceValue).map((story, index) => {
              return <div key={`default-${index}`}>{borderHandler(story)}</div>;
            })}
          </div>
          <div styleName="one-col-ads">{getSlot(type, component, () => null, () => null)}</div>
        </div>
        <div styleName="footer-ad-wrapper">
          {storyItems.length > sliceValue && (
            <div styleName="footer-wrapper">
              <LoadmoreButton template={footerButton} collection={collection} onClick={onClick} />
            </div>
          )}
          <div styleName="ad-wrapper">{footerSlotComp}</div>
        </div>
      </div>
    </div>
  );
};
OneColStoryList.propTypes = {
  /**  collection is the array of objects which is returning by API  */
  collection: PropTypes.object.isRequired,
  /** The config for the Row */
  config: PropTypes.shape({
    borderColor: PropTypes.string,
    theme: PropTypes.string,
    border: PropTypes.string,
    footerButton: PropTypes.string,
    // row title style
    collectionNameTemplate: PropTypes.string,
    // row title style colour
    collectionNameBorderColor: PropTypes.string
  })
};

export default StateProvider(OneColStoryList);
